import { useState } from 'react';
import { Link } from 'react-router-dom';
import { IconTree, IconSprout, IconDove, IconMeditation, IconAcademic, IconHandshake, IconPin, IconLeaf, IconMail } from '../components/Icons';
import { BrandScopus, BrandORCID, BrandWebOfScience, BrandResearchGate, BrandAcademia, BrandGoogleScholar, BrandSINTA, BrandInstagram, BrandFacebook, BrandLinkedIn } from '../components/BrandIcons';
import useScrollReveal from '../hooks/useScrollReveal';
import './Contact.css';

const inquiryTypes = ['Academic Collaboration', 'Speaking Invitation', 'Media Request', 'Partnership Proposal', 'Eco-Village Inquiry', 'General Inquiry'];

const academicProfiles = [
  { label: 'Scopus ID',      href: 'https://www.scopus.com/authid/detail.uri?authorId=6503908870', icon: BrandScopus },
  { label: 'ORCID',          href: 'https://orcid.org/0000-0002-3162-206X',                        icon: BrandORCID },
  { label: 'WOS ID',         href: 'https://www.webofscience.com/wos/author/record/GPP-4658-2022', icon: BrandWebOfScience },
  { label: 'ResearchGate',   href: 'https://www.researchgate.net/profile/Dicky-Sofjan',             icon: BrandResearchGate },
  { label: 'Academia.edu',   href: 'https://ugm.academia.edu/DSofjan',                             icon: BrandAcademia },
  { label: 'Google Scholar', href: 'https://scholar.google.com/citations?user=HpxRHN0AAAAJ&hl=en', icon: BrandGoogleScholar },
  { label: 'SINTA',          href: 'https://sinta.kemdiktisaintek.go.id/authors/profile/6007235/?view=rama', icon: BrandSINTA },
];

const socialProfiles = [
  { label: 'Instagram', href: 'https://www.instagram.com/sofjandicky/', icon: BrandInstagram },
  { label: 'Facebook',  href: 'https://web.facebook.com/dicky.sofjan/',  icon: BrandFacebook },
  { label: 'LinkedIn',  href: 'https://www.linkedin.com/in/dicky-sofjan-b11789118/', icon: BrandLinkedIn },
];

export default function Contact() {
  useScrollReveal();
  const [form, setForm] = useState({ name: '', email: '', org: '', type: '', subject: '', message: '' });
  const [errors, setErrors]   = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending]   = useState(false);

  const validate = () => {
    const e = {};
    if (!form.name.trim())    e.name    = 'Name is required';
    if (!form.email.trim())   e.email   = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(form.email)) e.email = 'Valid email required';
    if (!form.message.trim()) e.message = 'Message is required';
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;
    setSending(true);
    try {
      // 1. Save message copy to backend database (as backup)
      try {
        await fetch('/api/contact', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(form)
        });
      } catch (dbErr) {
        console.warn('Backup database logging failed:', dbErr);
      }

      // 2. Submit to FormSubmit.co for email delivery
      const res = await fetch('https://formsubmit.co/ajax/dickysofjan@ugm.ac.id', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          organisation: form.org || '—',
          inquiry_type: form.type || 'General Inquiry',
          subject: form.subject || 'No Subject',
          message: form.message
        })
      });

      if (!res.ok) throw new Error('FormSubmit error');
      setSubmitted(true);
    } catch {
      setErrors(prev => ({ ...prev, message: 'Failed to send message. Please try again later.' }));
    } finally {
      setSending(false);
    }
  };

  const field = (id, label, required) => ({
    id, label, required,
    value:    form[id] || '',
    onChange:  e => setForm(f => ({ ...f, [id]: e.target.value })),
    error:    errors[id],
  });

  return (
    <main>
      <section className="page-hero" aria-labelledby="contact-heading">
        <div className="container reveal reveal-up">
          <div className="contact-hero__grid">
            <div className="contact-hero__text">
              <span className="section-label">Get in Touch</span>
              <h1 id="contact-heading">Contact Dr. Dicky Sofjan</h1>
              <p className="section-subtitle" style={{ color: 'rgba(255,255,255,0.72)', maxWidth: '600px' }}>
                Dr. Sofjan welcomes academic inquiries, speaking invitations, media expert requests, and partnership proposals. 
                Please use the form below to initiate collaboration.
              </p>
            </div>
            <div className="contact-hero__portrait reveal reveal-photo delay-2">
              <div className="contact-hero__portrait-inner">
                <img src="/dr sofjan/5.jpeg" alt="Dr. Dicky Sofjan" />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div className="contact__grid">
             {/* Info column */}
            <aside className="contact__info reveal reveal-up">
              <h2 className="contact__info-title">Contact Information</h2>
              <div className="contact__info-items">
                <div className="contact__info-item"><span>Academic Email</span><a href="mailto:dickysofjan@ugm.ac.id">dickysofjan@ugm.ac.id</a></div>
                <div className="contact__info-item"><span>Secondary Email</span><a href="mailto:dickysofjan@yahoo.com">dickysofjan@yahoo.com</a></div>
                <div className="contact__info-item"><span>Phone / WA</span><a href="tel:+628121095544">+62 812-1095-544</a></div>
                <div className="contact__info-item"><span>Primary Base</span><strong>Universitas Gadjah Mada (UGM)</strong></div>
              </div>

              <div className="contact__profiles">
                <h4 className="profiles-group__title">Academic & Research Profiles</h4>
                <div className="profiles-row">
                  {academicProfiles.map(p => (
                    <a key={p.label} href={p.href} target="_blank" rel="noopener noreferrer" className="profile-chip profile-chip--academic">
                      <span className="profile-chip__abbr" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <p.icon size={24} />
                      </span>
                      <span>{p.label}</span>
                    </a>
                  ))}
                </div>
              </div>

              <div className="contact__profiles" style={{ marginTop: '2.5rem' }}>
                <h4 className="profiles-group__title">Social Media</h4>
                <div className="profiles-row">
                  {socialProfiles.map(p => (
                    <a key={p.label} href={p.href} target="_blank" rel="noopener noreferrer" className="profile-chip profile-chip--social">
                      <span className="profile-chip__abbr" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <p.icon size={24} />
                      </span>
                      <span>{p.label}</span>
                    </a>
                  ))}
                </div>
              </div>

              <div className="contact__special-card contact__special-card--navy">
                <h4 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}><IconMail size={20} /> Media &amp; Press Inquiries</h4>
                <p>Dr. Sofjan is available for expert commentary on religion, ethics, interfaith dialogue, Islamic politics, and environmental sustainability. Please include your deadline in your inquiry.</p>
                <a href="mailto:d.sofjan@ugm.ac.id?subject=Media%20Inquiry" className="btn btn-white btn-sm">Media Contact →</a>
              </div>

              <div className="contact__special-card contact__special-card--green">
                <h4 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}><IconLeaf size={20} /> Nglanggeran Eco-Village</h4>
                <p>For visit requests, research partnerships, or learning programme inquiries related to the Nglanggeran Eco-Village.</p>
                <Link to="/eco-village" className="btn btn-gold btn-sm">Eco-Village Inquiry →</Link>
              </div>
            </aside>

            {/* Form */}
            <div className="contact__form-wrap reveal reveal-up delay-2">
              {submitted ? (
                <div className="contact__success">
                  <div className="contact__success-icon">✓</div>
                  <h3>Message Sent</h3>
                  <p>Thank you for reaching out. Dr. Sofjan's office will respond within 3–5 business days.</p>
                  <button className="btn btn-primary btn-sm" onClick={() => { setSubmitted(false); setForm({ name:'',email:'',org:'',type:'',subject:'',message:'' }); }}>
                    Send Another Message
                  </button>
                </div>
              ) : (
                <form id="contact-form" className="contact__form" onSubmit={handleSubmit} noValidate>
                  <h2 className="contact__form-title">Send a Message</h2>
                  <div className="contact__form-row">
                    <FormField {...field('name','Full Name', true)} />
                    <FormField {...field('email','Email Address', true)} type="email" />
                  </div>
                  <div className="contact__form-row">
                    <FormField {...field('org','Organisation / Institution', false)} />
                    <div className="form-field">
                      <label htmlFor="inquiry-type">Nature of Inquiry</label>
                      <select id="inquiry-type" value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))} className="form-field__input">
                        <option value="">Select inquiry type…</option>
                        {inquiryTypes.map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                  </div>
                  <FormField {...field('subject','Subject', false)} />
                  <div className="form-field">
                    <label htmlFor="message">Message <span className="form-field__required">*</span></label>
                    <textarea
                      id="message"
                      value={form.message}
                      onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
                      rows={6}
                      placeholder="Please describe your inquiry in detail…"
                      className={`form-field__input ${errors.message ? 'form-field__input--error' : ''}`}
                    />
                    {errors.message && <span className="form-field__error">{errors.message}</span>}
                  </div>
                  <div className="contact__form-footer">
                    <p>Your information is handled with discretion and will only be used to respond to your inquiry. No marketing or third-party sharing.</p>
                    <button id="contact-submit" type="submit" className="btn btn-primary" disabled={sending}>
                      {sending ? 'Sending…' : 'Send Message →'}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Locations */}
      <section className="section section-alt" aria-labelledby="locations-heading">
        <div className="container">
          <div className="text-center" style={{ marginBottom: '2rem' }}>
            <span className="section-label">Locations</span>
            <h2 id="locations-heading" className="section-title">Where Dr. Sofjan Works</h2>
          </div>
          <div style={{ maxWidth: 400, margin: '0 auto' }}>
            {[
              { code: 'ID', name: 'Yogyakarta, Indonesia', type: 'Primary Location', desc: 'Primary base at Universitas Gadjah Mada. Also home to the Nglanggeran Eco-Village and the Yadema & YKPAI foundations.' },
            ].map(loc => (
              <div key={loc.code} className="contact__location-card">
                <div className="contact__location-code">{loc.code}</div>
                <div>
                  <div className="badge badge-gold" style={{ marginBottom: '0.5rem' }}>{loc.type}</div>
                  <h3>{loc.name}</h3>
                  <p>{loc.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}

function FormField({ id, label, required, value, onChange, error, type = 'text' }) {
  return (
    <div className="form-field">
      <label htmlFor={id}>{label} {required && <span className="form-field__required">*</span>}</label>
      <input
        id={id}
        type={type}
        value={value}
        onChange={onChange}
        className={`form-field__input ${error ? 'form-field__input--error' : ''}`}
        placeholder={`Your ${label.toLowerCase()}`}
      />
      {error && <span className="form-field__error">{error}</span>}
    </div>
  );
}
